<template>
    <div
        class="w-full h-auto flex flex-col gap-y-16 p-12 justify-center items-center"
    >
        <!-- HEADER -->
        <section class="flex-col justify-start items-center gap-3 flex">
            <h1
                class="text-black dark:text-white text-4xl font-semibold leading-10 text-center"
            >
                10xTravel Lounge Finder
            </h1>
            <p
                class="text-black/50 dark:text-slate-300 text-base font-normal leading-normal text-center"
            >
                Elevate your travel experience with access to comfortable,
                luxurious lounges.
            </p>
        </section>
        <!-- SEARCH FORM -->
        <section class="w-full md:w-11/12 lg:w-5/6 h-auto flex flex-col">
            <SearchTabs
                :activeTab="searchType"
                :tabs="tabs"
                @changeActiveTab="setActiveTab"
                class="mb-6"
            />
            <LoungeSearchForms :searchType="searchType" />
            <!-- CIRCLE BACKGROUND GRAPHIC -->
            <div
                class="absolute w-[275.53px] h-[275.53px] -inset-y-6 -end-32 circles -z-10"
            >
                <div
                    class="w-[204.09px] h-[204.09px] left-[27.70px] top-[19.23px] absolute rounded-full shadow border border-orange-400"
                ></div>
                <div
                    class="w-[249.91px] h-[249.91px] left-[4.79px] top-[-3.68px] absolute rounded-full shadow border border-orange-400"
                ></div>
                <div
                    class="w-[160.36px] h-[160.36px] left-[49.56px] top-[41.09px] absolute rounded-full shadow border border-orange-400"
                ></div>
                <div
                    class="w-[116.62px] h-[116.62px] left-[71.43px] top-[62.96px] absolute rounded-full shadow border border-orange-400"
                ></div>
            </div>
        </section>
        <!-- START FAVORITE LOUNGES SECTION -->
        <section class="favorites-carousel w-full max-w-6xl">
            <div>
                <CardCarousel
                    v-if="
                        !loadingFavorites &&
                        sortedFavoriteLounges?.length &&
                        sortedFavoriteLounges[0]?.all?.length
                    "
                    title="Favorite lounges"
                    :seeAllUrl="'/lounges/favorites'"
                    :numCards="sortedFavoriteLounges[0].all.length"
                    :cardWidth="256"
                    @seeAllClicked="() => setActiveFavoritesTab('Lounges')"
                    :cards="sortedFavoriteLounges[0].all"
                >
                    <template #carouselCard="{ card }">
                        <LoungeCard
                            :lounge="card"
                            :hideHours="true"
                            class="carousel__item"
                        />
                    </template>
                </CardCarousel>
                <!-- START EMPTY STATE - NO FAV LOUNGES FOUND -->
                <div v-else-if="!loadingFavorites">
                    <div class="flex justify-between pb-2">
                        <div
                            class="grow shrink basis-0 flex-col justify-end items-start gap-1 flex"
                        >
                            <h2
                                class="text-slate-800 dark:text-slate-100/95 text-lg font-medium leading-7"
                            >
                                Favorite lounges
                            </h2>
                        </div>
                        <Link
                            href="/lounges/favorites"
                            class="flex items-end text-slate-800 dark:text-slate-100 text-sm font-medium hover:text-slate-500 dark:hover:text-slate-400 active:text-slate-500 dark:active:text-slate-400 focus:outline-none"
                            role="button"
                        >
                            See all
                        </Link>
                    </div>
                    <div class="rounded-lg border border-slate-200 dark:border-slate-700 py-16">
                        <EmptyState
                            title="No favorites yet"
                            :description="`Tap the heart on any lounge to save it to your favorites.`"
                            :hasAction="false"
                        >
                            <template #image>
                                <div
                                    class="w-16 h-16 bg-slate-200 dark:bg-slate-700 rounded-full justify-center items-center flex text-slate-600 dark:text-slate-300"
                                >
                                    <Icon icon="heart" />
                                </div>
                            </template>
                        </EmptyState>
                    </div>
                </div>
                <div v-else class="h-24 w-full relative">
                    <LoadingState :loading="loadingFavorites" />
                </div>
            </div>
        </section>
        <!-- START FAVORITE AIRPORTS SECTION -->
        <section class="favorites-carousel w-full max-w-6xl relative">
            <CardCarousel
                v-if="
                    !loadingFavorites &&
                    sortedFavoriteAirports?.length &&
                    sortedFavoriteAirports[0]?.all?.length
                "
                title="Favorite airports"
                :seeAllUrl="'/lounges/favorites'"
                :numCards="sortedFavoriteAirports[0].all.length"
                :cardWidth="256"
                @seeAllClicked="() => setActiveFavoritesTab('Airports')"
                :cards="sortedFavoriteAirports[0].all"
            >
                <template #carouselCard="{ card, maxWidth }">
                    <AirportCard :airport="card" />
                </template>
            </CardCarousel>
            <!-- START EMPTY STATE - NO FAV AIRPORTS FOUND -->
            <div v-else-if="!loadingFavorites">
                <div class="flex justify-between pb-2">
                    <div
                        class="grow shrink basis-0 flex-col justify-end items-start gap-1 flex"
                    >
                        <h2
                            class="text-slate-800 dark:text-slate-100/95 text-lg font-medium leading-7"
                        >
                            Favorite Airports
                        </h2>
                    </div>
                    <Link
                        href="lounges/favorites"
                        class="flex items-end text-slate-800 dark:text-slate-100 text-sm font-medium hover:text-slate-500 dark:hover:text-slate-400 active:text-slate-500 dark:active:text-slate-400 focus:outline-none"
                        role="button"
                    >
                        See all
                    </Link>
                </div>
                <div class="rounded-lg border border-slate-200 dark:border-slate-700 py-16">
                    <EmptyState
                        title="No favorites yet"
                        :description="`Tap the heart on any airport to save it to your favorites.`"
                        :hasAction="false"
                    >
                        <template #image>
                            <div
                                class="w-16 h-16 bg-slate-200 dark:bg-slate-700 rounded-full justify-center items-center flex text-slate-600 dark:text-slate-300"
                            >
                                <Icon icon="heart" />
                            </div>
                        </template>
                    </EmptyState>
                </div>
            </div>
        </section>
    </div>
</template>

<script setup>
import 'vue3-carousel/dist/carousel.css';
import CardCarousel from '@/components/ui/card-carousel/card-carousel.vue';
import EmptyState from '@/components/ui/empty-state/empty-state.vue';
import Icon from '@/components/ui/icon/icon.vue';
import SearchTabs from '@/components/ui/search-tabs/tabs.vue';
import { Slide } from 'vue3-carousel';
import LoungeSearchForms from '@/components/tools/lounges/partials/lounge-search-forms/lounge-search-forms.vue';
import AirportCard from '@/components/tools/lounges/partials/airport-card/airport-card.vue';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';
import LoungeCard from '@/components/tools/lounges/partials/lounge-card/lounge-card.vue';
import { onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useLoungesStore } from '@/store/lounges';
import { useAppStore } from '@/store/app';
import { Link } from '@inertiajs/vue3';

const loadingFavorites = ref(true);
const tabs = ['Find a lounge', 'Trip wizard'];
const loungesStore = useLoungesStore();
const appStore = useAppStore();
const {
    setActiveAirport,
    getFilterOptions,
    getUserFavorites,
    setActiveFavoritesTab,
    setActiveTripLeg,
    setSearchType,
} = loungesStore;
const { sortedFavoriteAirports, sortedFavoriteLounges, searchType } =
    storeToRefs(loungesStore);

const setActiveTab = (tab) => {
    setSearchType(tab);
};

const getFavorites = async () => {
    loadingFavorites.value = true;

    try {
        // Fetch both lounge and airport favorites in parallel for better performance
        const startTime = performance.now();
        
        await Promise.all([
            getUserFavorites('lounge'),
            getUserFavorites('airport')
        ]);

        const endTime = performance.now();
        const loadTime = endTime - startTime;
        
        // Ensure minimum loading time for smooth UX (reduced from 2000ms to 300ms)
        const minLoadTime = 300;
        const remainingTime = Math.max(0, minLoadTime - loadTime);
        
        if (remainingTime > 0) {
            setTimeout(() => {
                loadingFavorites.value = false;
            }, remainingTime);
        } else {
            loadingFavorites.value = false;
        }
    } catch (error) {
        console.error('Error loading favorites:', error);
        loadingFavorites.value = false;
    }
};

onMounted(() => {
    setActiveAirport(null);
    setActiveTripLeg(1);
    getFilterOptions();
});

getFavorites();
</script>

<style lang="scss" scoped>
.circles {
    opacity: 0.3;
    mask-image: linear-gradient(to right, transparent, black);
}
</style>
